import { useState } from "react";
import { Card, Table, Button, Message } from "@/frontend/components/common";
import { tournamentGroupService } from "@/frontend/services";
import type { TournamentStandingResponseDTO } from "@/shared/dto";
import type { StageConfig } from "@/core/value-objects/StageConfig";
import type { ColumnsType } from "antd/es/table";

interface GroupStandingsTableProps {
  groupStandings: TournamentStandingResponseDTO[];
  stage: StageConfig;
  groupId: string;
  seasonId: string;
  knockoutStageId?: string;
  showAdvanceButton?: boolean;
  onAdvanceSuccess?: () => void;
}

export function GroupStandingsTable({
  groupStandings,
  stage,
  groupId,
  seasonId,
  knockoutStageId,
  showAdvanceButton = false,
  onAdvanceSuccess,
}: GroupStandingsTableProps) {
  const [advancing, setAdvancing] = useState(false);

  const handleAdvanceGroup = async () => {
    try {
      setAdvancing(true);
      const response = await tournamentGroupService.advanceGroup(groupId, {
        seasonId,
      });

      if (response.success && response.data) {
        Message.success(response.data.message || "Equipos avanzados exitosamente");
        onAdvanceSuccess?.();
      } else {
        Message.error("Error al avanzar equipos");
      }
    } catch (error) {
      console.error("Error advancing group:", error);
      Message.error("Error al avanzar equipos");
    } finally {
      setAdvancing(false);
    }
  };

  const sortedStandings = [...groupStandings].sort((a, b) => {
    if (a.rank !== null && b.rank !== null) return a.rank - b.rank;
    return b.points - a.points;
  });

  const groupName = sortedStandings[0]?.group?.name || "Grupo";

  const fullStandings = (() => {
    const perGroup = stage.structure.teamsPerGroup;
    if (!perGroup) return sortedStandings;

    const baseSeasonId = sortedStandings[0]?.seasonId || "";
    const baseGroupId = sortedStandings[0]?.groupId || null;
    const baseGroup = sortedStandings[0]?.group || null;

    const arranged: (TournamentStandingResponseDTO | null)[] = Array(perGroup).fill(null);

    sortedStandings.forEach((st) => {
      const idx = st.rank != null ? st.rank - 1 : arranged.findIndex((v) => v === null);
      if (idx >= 0 && idx < perGroup) arranged[idx] = st;
    });

    for (let i = 0; i < perGroup; i++) {
      if (!arranged[i]) {
        arranged[i] = {
          seasonId: baseSeasonId,
          groupId: baseGroupId,
          participantId: `undefined-${i + 1}`,
          participantName: "Por definir",
          participantLogo: null,
          teamId: null,
          played: 0,
          won: 0,
          drawn: 0,
          lost: 0,
          scoreFor: 0,
          scoreAgainst: 0,
          scoreDiff: 0,
          points: 0,
          winPercentage: 0,
          rank: i + 1,
          fairPlayScore: null,
          formHistory: [],
          group: baseGroup,
          country: { id: "", name: "", flag: null },
        };
      }
    }

    return arranged as TournamentStandingResponseDTO[];
  })();

  const columns: ColumnsType<TournamentStandingResponseDTO> = [
    {
      title: "Pos",
      dataIndex: "rank",
      key: "rank",
      width: 50,
      align: "center",
      render: (rank: number | null) => (
        <span className="font-bold text-gray-700">{rank ?? "-"}</span>
      ),
    },
    {
      title: "Equipo",
      key: "team",
      render: (_, record) => (
        <div className="flex items-center gap-2">
          {record.country?.flag && (
            <img
              src={record.country.flag}
              alt={record.country.name}
              className="w-6 h-4 object-cover rounded"
            />
          )}
          <span className="font-medium">
            {record.participantName || "Desconocido"}
          </span>
        </div>
      ),
    },
    {
      title: "PJ",
      dataIndex: "played",
      key: "played",
      width: 50,
      align: "center",
    },
    {
      title: "G",
      dataIndex: "won",
      key: "won",
      width: 50,
      align: "center",
      render: (won: number) => (
        <span className="text-green-600 font-medium">{won}</span>
      ),
    },
    {
      title: "E",
      dataIndex: "drawn",
      key: "drawn",
      width: 50,
      align: "center",
      render: (drawn: number) => (
        <span className="text-yellow-600 font-medium">{drawn}</span>
      ),
    },
    {
      title: "P",
      dataIndex: "lost",
      key: "lost",
      width: 50,
      align: "center",
      render: (lost: number) => (
        <span className="text-red-600 font-medium">{lost}</span>
      ),
    },
    {
      title: "GF",
      dataIndex: "scoreFor",
      key: "scoreFor",
      width: 50,
      align: "center",
    },
    {
      title: "GC",
      dataIndex: "scoreAgainst",
      key: "scoreAgainst",
      width: 50,
      align: "center",
    },
    {
      title: "Dif",
      dataIndex: "scoreDiff",
      key: "scoreDiff",
      width: 50,
      align: "center",
      render: (diff: number) => (
        <span
          className={
            diff > 0
              ? "text-green-600 font-bold"
              : diff < 0
                ? "text-red-600 font-bold"
                : ""
          }
        >
          {diff > 0 ? `+${diff}` : diff}
        </span>
      ),
    },
    {
      title: "FP",
      dataIndex: "fairPlayScore",
      key: "fairPlayScore",
      width: 50,
      align: "center",
      render: (fp: number | null | undefined) => {
        if (fp == null) {
          return <span className="text-gray-300">-</span>;
        }
        return <span className="text-gray-500">{fp}</span>;
      },
    },
    {
      title: "Pts",
      dataIndex: "points",
      key: "points",
      width: 60,
      align: "center",
      render: (points: number) => (
        <span className="text-blue-700 font-bold text-lg">{points}</span>
      ),
    },
  ];

  return (
    <Card
      title={
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-500" />
          <span className="font-bold text-gray-800">{groupName}</span>
        </div>
      }
      className="shadow-sm"
    >
      <Table
        columns={columns}
        dataSource={fullStandings}
        rowKey="participantId"
        pagination={false}
        size="small"
        rowClassName={(record, index) => {
          const promotedCount = stage.progression?.promotedCount || 0;
          if (index < promotedCount) {
            return "bg-green-50 hover:bg-green-100";
          }
          return "";
        }}
      />
      {showAdvanceButton && (
        <div className="mt-4 flex justify-end">
          <Button
            type="primary"
            onClick={handleAdvanceGroup}
            loading={advancing}
            disabled={advancing}
          >
            Avanzar primeros 2
          </Button>
        </div>
      )}
    </Card>
  );
}
